import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { BadgeCheck, FileBadge, X } from "lucide-react";
import PageHero from "@/components/PageHero";
import PageLinkCTA from "@/components/PageLinkCTA";
import { Reveal } from "@/components/Reveal";
import { EASE } from "@/components/Reveal";
import { CERTIFICATIONS } from "@/data/certifications";

// Certificate document placeholders render where actual certificate
// photos/documents will be uploaded later (data/certifications.js).
function CertificatePlaceholder({ title, large = false }) {
  return (
    <div className="relative flex h-full w-full flex-col items-center justify-center gap-3 overflow-hidden bg-slate-100 p-6 text-center">
      <div className="absolute inset-0 blueprint-grid opacity-70" aria-hidden="true" />
      <span
        className={`flex items-center justify-center rounded-full border border-dashed border-brand-emeraldLight/50 bg-white/80 ${
          large ? "h-20 w-20" : "h-14 w-14"
        }`}
      >
        <FileBadge className={`${large ? "h-9 w-9" : "h-6 w-6"} text-brand-emeraldDark`} />
      </span>
      <span className="font-mono text-[10px] uppercase tracking-[0.26em] text-brand-emeraldDark">
        Certificate Placeholder
      </span>
      <span className="text-xs text-slate-500">{title} — document to be added</span>
    </div>
  );
}

export default function Certifications() {
  const [active, setActive] = useState(null);

  useEffect(() => {
    if (!active) return undefined;
    const onKey = (e) => e.key === "Escape" && setActive(null);
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    if (window.__lenis) window.__lenis.stop();
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
      if (window.__lenis) window.__lenis.start();
    };
  }, [active]);

  return (
    <>
      <PageHero
        eyebrow="Certifications & Registrations"
        title="Compliance, On the Record"
        description="Our certifications and registrations — presented transparently, with actual certificate documents to be displayed as they are published."
      />

      <section className="relative section-pad bg-paper text-ink" data-testid="certifications-section">
        <div className="absolute inset-0 blueprint-grid-light" aria-hidden="true" />
        <div className="wrap relative grid gap-7 md:grid-cols-3">
          {CERTIFICATIONS.map((cert, i) => (
            <Reveal key={cert.id} delay={i * 0.1} className="h-full">
              <article
                className="group flex h-full flex-col overflow-hidden rounded-2xl border border-navy-700/10 bg-white shadow-sm transition-all duration-500 hover:-translate-y-2 hover:border-brand-emerald/40 hover:shadow-[0_28px_70px_rgba(16,185,129,0.2)]"
                data-testid={`certification-card-${cert.id}`}
              >
                <div className="relative aspect-[4/3] overflow-hidden border-b border-navy-700/10">
                  <div className="h-full w-full transition-transform duration-700 group-hover:scale-[1.05]">
                    {cert.image ? (
                      <img src={cert.image} alt={`${cert.title} certificate`} loading="lazy" className="h-full w-full bg-white object-contain p-3" />
                    ) : (
                      <CertificatePlaceholder title={cert.title} />
                    )}
                  </div>
                  <span
                    aria-hidden="true"
                    className="absolute inset-x-0 bottom-0 h-[3px] bg-gradient-to-r from-brand-emerald via-brand-blueGlow to-brand-emerald opacity-0 transition-opacity duration-500 group-hover:opacity-100"
                  />
                </div>
                <div className="flex flex-1 flex-col p-6">
                  <div className="flex items-center gap-2">
                    <BadgeCheck className="h-4 w-4 text-brand-emerald" />
                    <span className="font-mono text-[10px] uppercase tracking-[0.24em] text-slate-500">
                      {cert.subtitle}
                    </span>
                  </div>
                  <h2 className="mt-3 font-display text-2xl font-bold uppercase tracking-wide">{cert.title}</h2>
                  <p className="mt-3 flex-1 text-sm leading-relaxed text-slate-700">{cert.description}</p>
                  <button
                    type="button"
                    onClick={() => setActive(cert)}
                    data-testid={`certification-view-${cert.id}`}
                    className="btn btn-outline btn-sm mt-6 !border-navy-700/25 !text-navy-900 hover:!border-brand-emerald hover:!text-brand-emerald"
                  >
                    View Certificate
                  </button>
                </div>
              </article>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.2}>
          <p className="wrap relative mt-10 text-center text-xs leading-relaxed text-slate-500">
            Certificate numbers, accreditation details and issuing bodies are intentionally not shown until the
            actual documents are published.
          </p>
        </Reveal>
      </section>

      <section className="wrap section-pad">
        <PageLinkCTA />
      </section>

      {/* Lightbox */}
      <AnimatePresence>
        {active && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[80] flex items-center justify-center p-4 sm:p-10"
            data-testid={`certification-lightbox-${active.id}`}
          >
            <div className="absolute inset-0 bg-white/85 backdrop-blur-sm" onClick={() => setActive(null)} aria-hidden="true" />
            <motion.div
              role="dialog"
              aria-modal="true"
              aria-label={`${active.title} certificate`}
              initial={{ opacity: 0, y: 40, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 24, scale: 0.97 }}
              transition={{ duration: 0.45, ease: EASE }}
              className="relative max-h-[88vh] w-full max-w-3xl overflow-y-auto rounded-3xl border border-navy-900/12 bg-white p-6 sm:p-8"
            >
              <button
                type="button"
                onClick={() => setActive(null)}
                aria-label="Close certificate viewer"
                className="absolute right-4 top-4 z-10 flex h-10 w-10 items-center justify-center rounded-full border border-navy-900/15 bg-[#F3F7FB]/70 text-slate-600 transition hover:border-brand-emeraldLight hover:text-brand-emeraldDark"
              >
                <X className="h-5 w-5" />
              </button>
              <div className="aspect-[4/3] overflow-hidden rounded-2xl border border-navy-900/10">
                {active.image ? (
                  <img src={active.image} alt={`${active.title} certificate`} className="h-full w-full object-contain" />
                ) : (
                  <CertificatePlaceholder title={active.title} large />
                )}
              </div>
              <div className="mt-6 text-center">
                <h3 className="font-display text-2xl font-bold uppercase tracking-wide text-ink">{active.title}</h3>
                <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.24em] text-brand-emeraldDark">
                  {active.subtitle}
                </p>
                <p className="mx-auto mt-4 max-w-lg text-sm leading-relaxed text-slate-500">{active.description}</p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
